import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import GaussianSplatMedia from "./GaussianSplatMedia";

const ProjectLightbox = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onPrev,
  onNext,
}) => {
  const media = images?.[currentIndex];

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onNext();
      if (event.key === "ArrowLeft") onPrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, onNext, onPrev]);

  if (!isOpen || !media) return null;

  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 top-16 z-30 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Project media viewer"
    >
      <button
        type="button"
        className="absolute right-4 top-4 z-10 rounded-full bg-black/30 px-3 py-2 text-2xl text-white transition hover:bg-black/50 hover:text-gray-200 sm:right-6 sm:top-6"
        onClick={onClose}
        aria-label="Close image viewer"
      >
        ✕
      </button>

      <div className="pointer-events-none relative flex h-full w-full items-center justify-center">
        {media.type === "gsplat" ? (
          <div className="pointer-events-auto h-full w-full overflow-hidden rounded-[var(--radius-md)] border border-line bg-black">
            <GaussianSplatMedia asset={media} fullScreen={true} />
          </div>
        ) : (
          <img
            src={media.src}
            alt={media.alt || "Project screenshot"}
            className="pointer-events-auto block h-auto max-h-full w-auto max-w-full object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        )}

        {media.caption && (
          <div className={`pointer-events-auto absolute inset-x-0 ${media.type === "gsplat" ? "top-0 bg-gradient-to-b" : "bottom-0 bg-gradient-to-t"} from-black/90 to-transparent px-6 py-5 text-center text-white`}>
            <p className="text-sm font-mono">{media.caption}</p>
            {images.length > 1 && (
              <p className="mt-1 text-xs text-gray-300">
                {currentIndex + 1} / {images.length}
              </p>
            )}
          </div>
        )}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-line bg-white/10 p-3 text-2xl text-white transition hover:bg-white/20 sm:left-6"
            onClick={(event) => {
              event.stopPropagation();
              onPrev();
            }}
            aria-label="Previous image"
          >
            ‹
          </button>
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-line bg-white/10 p-3 text-2xl text-white transition hover:bg-white/20 sm:right-6"
            onClick={(event) => {
              event.stopPropagation();
              onNext();
            }}
            aria-label="Next image"
          >
            ›
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

export default ProjectLightbox;
